import { assertIdempotency, buildApiPath, type ApiEndpointContract } from "./api-contracts";
import { coachingApi } from "./coaching-api";
import { coachingEndpoints } from "./coaching-contracts";
import { appConfig } from "../lib/config";
import { toCoachingProfileDto } from "./coaching-live-auth";
import {
  liveArtifactRow,
  liveCohortRow,
  liveCompanyRow,
  liveHistoryEventRow,
  liveParticipantRow,
  liveProgramRow,
  liveResourceRow,
  liveSessionRow,
  liveUserRow,
  toLiveListResponse,
  toLiveOperationResult,
  toLiveRecordResponse,
  type LiveRowMapper
} from "./coaching-live-readmodels";
import { clearCoachingOrgId, getCoachingOrgId, setCoachingProfileHint } from "./coaching-org-context";
import type { ApiBlobResponse } from "./api-client";
import type { CoachingProfileDto, ListResponse } from "./coaching-dtos";

// The live coaching client exposes both `request` (JSON) and `requestText`
// (text/csv). The mock + test requesters only implement `request`, so
// `requestText` is OPTIONAL on the shared requester type; the CSV export path
// feature-detects it and falls back to the JSON acknowledgement in mock mode.
export interface CoachingRequester {
  request: (typeof coachingApi)["request"];
  requestText?: (typeof coachingApi)["requestText"];
  requestBlob?: (typeof coachingApi)["requestBlob"];
}
export type Requester = CoachingRequester;
export type Query = Record<string, string | number | boolean | null | undefined>;
export type PathParams = Record<string, string | number | boolean | null | undefined>;
export type EndpointKey = keyof typeof coachingEndpoints;

export interface ContractRequestOptions {
  pathParams?: PathParams;
  query?: Query;
  body?: unknown;
  idempotencyKey?: string;
}

/**
 * Shared coaching transport: resolves the contract, asserts idempotency, injects
 * the live `orgId` query, and issues the request. Exported so the Z6 service
 * family can reuse the exact same org-injection + idempotency discipline without
 * re-implementing it.
 */
export async function requestContract<TResponse>(requester: Requester, key: EndpointKey, options: ContractRequestOptions = {}) {
  const contract = coachingEndpoints[key];
  assertIdempotency(contract, options.idempotencyKey);
  if (requiresLiveOrgId(contract) && !getCoachingOrgId()) await ensureLiveOrgId(requester);
  return requester.request<TResponse>(buildApiPath(contract.pathTemplate, options.pathParams, withLiveOrgId(contract, options.query)), {
    method: contract.method,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    idempotencyKey: options.idempotencyKey
  });
}
export async function requestBlobContract(
  requester: Requester,
  key: EndpointKey,
  options: ContractRequestOptions = {}
): Promise<ApiBlobResponse> {
  const contract = coachingEndpoints[key];
  assertIdempotency(contract, options.idempotencyKey);
  if (requiresLiveOrgId(contract) && !getCoachingOrgId()) await ensureLiveOrgId(requester);
  const path = buildApiPath(contract.pathTemplate, options.pathParams, withLiveOrgId(contract, options.query));
  if (typeof requester.requestBlob === "function") {
    return requester.requestBlob(path, {
      method: contract.method,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      idempotencyKey: options.idempotencyKey
    });
  }
  const payload = await requester.request<unknown>(path, {
    method: contract.method,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    idempotencyKey: options.idempotencyKey
  });
  if (payload && typeof payload === "object" && "blob" in payload && payload.blob instanceof Blob) {
    return payload as ApiBlobResponse;
  }
  const text = typeof payload === "string" ? payload : JSON.stringify(payload ?? "");
  return { blob: new Blob([text], { type: "text/html; charset=utf-8" }), filename: null, contentType: "text/html; charset=utf-8" };
}

export function requiresLiveOrgId(contract: ApiEndpointContract) {
  return appConfig.dataMode === "live" && contract.lane !== "platform-auth" && !contract.key.startsWith("platformOrganizations.") && contract.key !== "auth.me";
}

/**
 * On a fresh page load, product queries race the `/auth/me` profile query; the
 * org context they all need is only known after that projection resolves. The
 * bootstrap dedupes concurrent resolutions and never throws — without a
 * session the product call proceeds org-less and surfaces the backend error.
 */
let liveOrgIdBootstrap: Promise<CoachingProfileDto> | null = null;

export function ensureLiveOrgId(requester: Requester) {
  if (getCoachingOrgId()) return Promise.resolve();
  return readLiveAuthProfile(requester)
    .then(() => undefined)
    .catch(() => undefined);
}

export function readLiveAuthProfile(requester: Requester): Promise<CoachingProfileDto> {
  const bootstrap = liveOrgIdBootstrap ?? (liveOrgIdBootstrap = requestContract<unknown>(requester, "auth.me")
    .then((payload) => {
      const profile = toCoachingProfileDto(payload);
      const storedOrgId = getCoachingOrgId();
      const storedMembership = profile.memberships?.find(
        (membership) => membership.orgId === storedOrgId && membership.coachingRole
      );
      if (storedMembership) {
        const selectedRole = storedMembership.coachingRole ?? profile.role;
        setCoachingProfileHint(storedMembership.orgId, selectedRole);
        return { ...profile, orgId: storedMembership.orgId, role: selectedRole };
      }
      setCoachingProfileHint(profile.orgId ?? null, profile.role);
      return profile;
    })
    .catch((error) => {
      clearCoachingOrgId();
      throw error;
    })
    .finally(() => {
      liveOrgIdBootstrap = null;
    }));
  return bootstrap;
}

/**
 * Every live coaching product route requires a positive-integer `orgId` query
 * parameter; without it the backend returns 400 CO_VALIDATION_FAILED. The org
 * is resolved from the `/auth/me` membership projection (the only product
 * route that accepts an org-less call) and injected here for live requests.
 */
export function withLiveOrgId(contract: ApiEndpointContract, query?: Query): Query | undefined {
  if (appConfig.dataMode !== "live") return query;
  if (contract.lane === "platform-auth" || contract.key === "auth.me") return query;
  if (query?.orgId != null) return query;
  const orgId = getCoachingOrgId();
  return orgId ? { ...query, orgId } : query;
}

const liveRowMappers: Record<string, LiveRowMapper<unknown>> = {
  companies: liveCompanyRow,
  programs: liveProgramRow,
  cohorts: liveCohortRow,
  participants: liveParticipantRow,
  sessions: liveSessionRow,
  reports: liveArtifactRow,
  resources: liveResourceRow,
  history: liveHistoryEventRow,
  users: liveUserRow
};

function liveRowMapperFor<T>(key: EndpointKey): LiveRowMapper<T> {
  const mapper = liveRowMappers[String(key).split(".")[0]] ?? ((row: Record<string, unknown>) => row);
  return mapper as LiveRowMapper<T>;
}

export function list<T>(requester: Requester, key: EndpointKey, query?: Query): Promise<ListResponse<T>> {
  return listWithParams<T>(requester, key, { query });
}

export async function listWithParams<T>(
  requester: Requester,
  key: EndpointKey,
  options: ContractRequestOptions = {}
): Promise<ListResponse<T>> {
  return toLiveListResponse<T>(await requestContract<unknown>(requester, key, options), liveRowMapperFor<T>(key));
}

export async function record<T>(requester: Requester, key: EndpointKey, options: ContractRequestOptions = {}): Promise<T> {
  return toLiveRecordResponse<T>(await requestContract<unknown>(requester, key, options), liveRowMapperFor<T>(key));
}

export async function operationContract(requester: Requester, key: EndpointKey, options: ContractRequestOptions = {}) {
  return toLiveOperationResult(await requestContract<unknown>(requester, key, options));
}
